import { useEffect, useState } from "react";
import AppHeader from "@/components/AppHeader";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pill } from "@/components/report/atoms";
import { useAuth } from "@/contexts/AuthContext";
import { listUsers } from "@/lib/activityApi";
import { updateUserRole, sendAllBdrDigests, sendTeamDigest } from "@/lib/teamApi";
import { toast } from "sonner";
import { Shield, User as UserIcon, Mail, Users as UsersIcon, Send } from "lucide-react";

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listUsers();
      setUsers(data);
    } catch (err) {
      toast.error("Could not load team", { description: err?.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Only managers can access this page
  if (user && (user.role || "bdr") !== "manager") {
    return <Navigate to="/" replace/>;
  }

  const onChangeRole = async (u, newRole) => {
    if (u.role === newRole) return;
    setBusy(u.user_id);
    try {
      await updateUserRole(u.user_id, newRole);
      toast.success(`${u.name} · ${newRole.toUpperCase()}`);
      load();
    } catch (err) {
      toast.error("Role change failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(null);
    }
  };

  const onSendAllBDRs = async () => {
    if (!window.confirm(`Send individual digest emails to all ${users.length} users?`)) return;
    setBusy("send-bdrs");
    try {
      const r = await sendAllBdrDigests();
      const delivered = r.recipients.filter((x) => !x.error).length;
      const failed = r.recipients.length - delivered;
      toast.success(`Sent ${delivered} digest${delivered === 1 ? "" : "s"}${failed ? ` · ${failed} failed` : ""}`);
    } catch (err) {
      toast.error("Bulk send failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(null);
    }
  };

  const onSendTeamDigest = async () => {
    setBusy("send-team");
    try {
      await sendTeamDigest();
      toast.success("Team digest sent to all managers");
    } catch (err) {
      toast.error("Send failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen radar-grid-bg">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-6 md:px-8 py-10 md:py-14">
        <section className="mb-8">
          <div className="kpi-label mb-3">/ TEAM · ADMIN</div>
          <h1 className="font-display text-3xl md:text-5xl tracking-tighter text-white leading-[1.05]">
            Manage roles, <span className="text-[#0A84FF]">trigger digests.</span>
          </h1>
          <p className="text-[#A3A3A3] mt-3 max-w-2xl text-[15px]">
            Promote team members to managers (who see team-wide data, send digests, and manage roles).
            BDRs only see their own pipeline.
          </p>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          <div className="bg-[#141414] border border-[#262626] rounded-sm p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-sm border border-[#0A84FF]/40 bg-[#0A84FF]/10 flex items-center justify-center shrink-0">
              <Send className="w-5 h-5 text-[#0A84FF]" strokeWidth={1.7}/>
            </div>
            <div className="flex-1">
              <div className="kpi-label">/ BROADCAST · BDR DIGESTS</div>
              <p className="text-sm text-[#A3A3A3] mt-1">Email every user their individual 7-day digest.</p>
              <Button
                type="button"
                onClick={onSendAllBDRs}
                disabled={busy === "send-bdrs" || users.length === 0}
                data-testid="send-all-bdrs-btn"
                className="mt-3 h-9 px-4 rounded-sm bg-[#0A84FF] hover:bg-[#409CFF] text-white gap-2"
              >
                <span className="text-xs font-mono-data tracking-wider uppercase">{busy === "send-bdrs" ? "Sending…" : `Send to ${users.length}`}</span>
              </Button>
            </div>
          </div>
          <div className="bg-[#141414] border border-[#262626] rounded-sm p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-sm border border-[#32D74B]/40 bg-[#32D74B]/10 flex items-center justify-center shrink-0">
              <Shield className="w-5 h-5 text-[#32D74B]" strokeWidth={1.7}/>
            </div>
            <div className="flex-1">
              <div className="kpi-label">/ MANAGER DIGEST</div>
              <p className="text-sm text-[#A3A3A3] mt-1">Send the team rollup to every manager.</p>
              <Button
                type="button"
                onClick={onSendTeamDigest}
                disabled={busy === "send-team"}
                data-testid="send-team-digest-btn"
                className="mt-3 h-9 px-4 rounded-sm bg-transparent hover:bg-[#1F1F1F] text-white border border-[#262626] hover:border-[#404040]"
              >
                <span className="text-xs font-mono-data tracking-wider uppercase">{busy === "send-team" ? "Sending…" : "Send to managers"}</span>
              </Button>
            </div>
          </div>
        </section>

        <section>
          <div className="kpi-label mb-3">/ TEAM ROSTER · {users.length} MEMBER{users.length === 1 ? "" : "S"}</div>
          {loading ? (
            <div className="text-sm text-[#737373]">Loading…</div>
          ) : (
            <div className="border border-[#262626] rounded-sm overflow-hidden" data-testid="team-roster-table">
              <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-[#1F1F1F] border-b border-[#262626] kpi-label">
                <div className="col-span-6">Member</div>
                <div className="col-span-3">Current Role</div>
                <div className="col-span-3 text-right">Change</div>
              </div>
              {users.map((u, idx) => (
                <div key={u.user_id} className="grid grid-cols-12 gap-3 px-4 py-3 items-center border-b border-[#262626] last:border-b-0" data-testid={`team-row-${idx}`}>
                  <div className="col-span-6 flex items-center gap-3 min-w-0">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-8 h-8 rounded-sm object-cover"/>
                    ) : (
                      <div className="w-8 h-8 rounded-sm bg-[#0A84FF]/20 border border-[#0A84FF]/40 flex items-center justify-center">
                        <UserIcon className="w-3.5 h-3.5 text-[#0A84FF]" strokeWidth={1.8}/>
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-white font-display tracking-tight truncate">{u.name}</div>
                      <div className="kpi-label flex items-center gap-1 truncate"><Mail className="w-3 h-3"/> {u.email}</div>
                    </div>
                  </div>
                  <div className="col-span-3">
                    <Pill tone={u.role === "manager" ? "green" : "neutral"}>{(u.role || "bdr").toUpperCase()}</Pill>
                  </div>
                  <div className="col-span-3 flex justify-end">
                    <Select
                      value={u.role || "bdr"}
                      onValueChange={(v) => onChangeRole(u, v)}
                      disabled={busy === u.user_id || u.user_id === user?.user_id}
                    >
                      <SelectTrigger className="h-9 bg-[#0A0A0A] border-[#262626] rounded-sm text-sm focus:ring-1 focus:ring-[#0A84FF] w-32" data-testid={`role-select-${idx}`}>
                        <SelectValue/>
                      </SelectTrigger>
                      <SelectContent className="bg-[#141414] border-[#262626] rounded-sm">
                        <SelectItem value="manager" className="text-sm font-mono-data">Manager</SelectItem>
                        <SelectItem value="bdr" className="text-sm font-mono-data">BDR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 flex items-center gap-2 text-[11px] text-[#525252] font-mono-data">
            <UsersIcon className="w-3 h-3"/>
            <span>YOU CANNOT CHANGE YOUR OWN ROLE · AT LEAST ONE MANAGER MUST REMAIN</span>
          </div>
        </section>
      </main>
    </div>
  );
}
